import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Button,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import {
  Search,
  FilterList,
  LocationOn,
  History,
  Close,
  TrendingUp,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import DesignSystem from '../designSystem';
import { useAuth } from '../contexts/AuthContext';
import { getApiLanguage } from '../config/apiLanguage';
import { formatDistanceKm } from '../utils/distanceDisplay';
import {
  fetchCategories,
  fetchUpNext,
  getBusinessLogoUrl,
  getCategoryIconUrl,
  ApiBusiness,
  searchByString,
  searchCategoriesByString,
  fetchSearchHistory,
  clearSearchHistory,
  addToSearchHistory,
} from '../services/api';
import { EmptyState, EntityRow, SectionLabel } from '../components/ui';
import { isTestEntityName, preferNonTestEntities } from '../utils/testEntityName';
import { useT } from '../i18n/useT';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && children}
    </Box>
  );
}

interface RecentSearch {
  id: string;
  query: string;
  type: 'visi' | 'category';
  timestamp: Date;
}

export default function SearchPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const t = useT();
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('q') || '');
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);
  const [businesses, setBusinesses] = useState<ApiBusiness[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [suggestedLoading, setSuggestedLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<ApiBusiness[]>([]);
  const businessesRef = useRef<ApiBusiness[]>([]);

  useEffect(() => {
    const q = searchParams.get('q') || '';
    setSearchQuery((prev) => (prev === q ? prev : q));
  }, [searchParams]);

  const updateQuery = (next: string) => {
    setSearchQuery(next);
    const trimmed = next.trim();
    if (trimmed) {
      setSearchParams({ q: trimmed }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  // Lightweight mount data — avoid getUserCategories (full home feed) on Search.
  useEffect(() => {
    if (!user?.token) return;
    let cancelled = false;
    setSuggestedLoading(true);
    Promise.all([
      fetchCategories(getApiLanguage(), user.token),
      fetchUpNext(user.token, getApiLanguage(), 12),
      fetchSearchHistory(user.token, getApiLanguage()).catch(() => ({ history: [], businesses: [] })),
    ])
      .then(([cats, upNext, history]) => {
        if (cancelled) return;
        setCategories(
          (cats.categories || []).map((cat) => {
            const iconKey = cat.image || (cat as { icon?: string }).icon;
            return {
              id: cat.id.toString(),
              name: cat.name,
              icon: iconKey ? getCategoryIconUrl(iconKey) : null,
              color: cat.color,
            };
          })
        );
        const suggested = upNext.businesses || [];
        setBusinesses(suggested);
        businessesRef.current = suggested;
        const fromHistory = (history.history || []).map((h) => ({
          id: String(h.id),
          query: h.query || h.business?.name || (h.business_id ? `Business #${h.business_id}` : 'Search'),
          type: (h.business_id || h.business ? 'visi' : 'category') as 'visi' | 'category',
          timestamp: new Date(h.created_at),
        }));
        const fromBusinesses = (history.businesses || []).map((b) => ({
          id: String(b.id),
          query: b.name || b.username || `Business #${b.id}`,
          type: 'visi' as const,
          timestamp: new Date(),
        }));
        setRecentSearches((fromHistory.length ? fromHistory : fromBusinesses).slice(0, 10));
      })
      .catch((error) => {
        console.error('Error loading search data:', error);
      })
      .finally(() => {
        if (!cancelled) setSuggestedLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.token]);

  // Search businesses via API when query changes
  useEffect(() => {
    if (!searchQuery.trim() || !user?.token) {
      setSearchResults([]);
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(async () => {
      setIsLoading(true);
      try {
        if (tabValue === 0) {
          const response = await searchByString(
            searchQuery,
            user.token,
            undefined,
            undefined,
            getApiLanguage(),
            controller.signal
          );
          if (!controller.signal.aborted) {
            setSearchResults(response.businesses || []);
          }
        } else {
          const response = await searchCategoriesByString(
            searchQuery,
            user.token,
            getApiLanguage(),
            controller.signal
          );
          if (!controller.signal.aborted && response.categories) {
            setCategories(
              response.categories.map((cat) => {
                const iconKey = cat.image || cat.icon;
                return {
                  id: cat.id.toString(),
                  name: cat.name,
                  icon: iconKey ? getCategoryIconUrl(iconKey) : null,
                  color: cat.color,
                };
              })
            );
          }
        }
      } catch (error) {
        if ((error as { name?: string })?.name === 'AbortError') return;
        console.error('Search error:', error);
        const query = searchQuery.toLowerCase();
        setSearchResults(
          businessesRef.current.filter(
            (b) =>
              b.name?.toLowerCase().includes(query) ||
              b.username?.toLowerCase().includes(query) ||
              b.description?.toLowerCase().includes(query)
          )
        );
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    }, 280);

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [searchQuery, tabValue, user?.token]);
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSearch = (query: string) => {
    updateQuery(query);

    // Add to recent searches
    if (query.trim()) {
      const newSearch: RecentSearch = {
        id: Date.now().toString(),
        query: query.trim(),
        type: tabValue === 0 ? 'visi' : 'category',
        timestamp: new Date(),
      };
      setRecentSearches(prev => [newSearch, ...prev.filter(s => s.query !== query).slice(0, 9)]);
    }
  };

  const handleClearRecentSearches = async () => {
    if (user?.token) {
      try {
        await clearSearchHistory(user.token, getApiLanguage());
      } catch (error) {
        console.error('Error clearing search history:', error);
      }
    }
    setRecentSearches([]);
  };

  const handleRemoveRecentSearch = (id: string) => {
    setRecentSearches(prev => prev.filter(s => s.id !== id));
  };

  const handleBusinessClick = async (business: ApiBusiness) => {
    // Add to search history
    if (user?.token) {
      try {
        await addToSearchHistory(business.id, user.token, getApiLanguage());
      } catch (error) {
        console.error('Error adding to search history:', error);
      }
    }
    const username = business.username?.replace('@', '') || business.id.toString();
    navigate(`/${username}`);
  };

  const handleCategoryClick = (categoryId: string) => {
    // Navigate to home with category filter
    navigate(`/homepage?category=${categoryId}`);
  };

  const filteredCategories = searchQuery.trim()
    ? categories.filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : categories;

  return (
    <AppShell title={t('navSearch')} hideTitle maxWidth="feed" showCompanion>
        {/* Search controls — top (iOS-aligned), not bottom-fixed on desktop */}
        <Box
          sx={{
            position: { xs: 'sticky', md: 'static' },
            top: { xs: 0, md: 'auto' },
            zIndex: 5,
            px: 0,
            pt: 0.5,
            pb: 1.75,
            backgroundColor: DesignSystem.colors.canvas,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              fullWidth
              placeholder={t('searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => updateQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch(searchQuery);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: DesignSystem.colors.textSecondary }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => updateQuery('')}>
                      <Close sx={{ fontSize: 18 }} />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: DesignSystem.borderRadius.pill,
                  backgroundColor: DesignSystem.colors.surface,
                  '& fieldset': { border: 'none' },
                  '&:hover fieldset': { border: 'none' },
                  '&.Mui-focused fieldset': { border: 'none' },
                },
                '& input': { color: DesignSystem.colors.textPrimary, py: 1.35 },
              }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterList />}
              onClick={() => navigate('/search/advanced')}
              aria-label={t('searchAdvancedAria')}
              sx={{
                flexShrink: 0,
                backgroundColor: DesignSystem.colors.surface,
                border: 0,
                color: DesignSystem.colors.primary,
                borderRadius: DesignSystem.borderRadius.pill,
                px: 1.75,
                minHeight: 48,
                textTransform: 'none',
                fontWeight: 700,
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: DesignSystem.colors.surfaceElevated,
                  border: 0,
                },
              }}
            >
              {t('searchAdvanced')}
            </Button>
          </Box>
        </Box>

        <Box sx={{ mb: 1.5 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              minHeight: 'auto',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 700,
                fontSize: 15,
                color: DesignSystem.colors.textSecondary,
                minHeight: 40,
                px: 0.5,
                mr: 2,
                '&.Mui-selected': { color: DesignSystem.colors.textPrimary },
              },
              '& .MuiTabs-indicator': { display: 'none' },
            }}
          >
            <Tab label={t('searchTabVisi')} />
            <Tab label={t('searchTabCategories')} />
          </Tabs>
        </Box>

        {!searchQuery && recentSearches.filter((s) => !isTestEntityName(s.query)).length > 0 && (
          <Box sx={{ px: 0, pb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <SectionLabel>{t('searchRecent')}</SectionLabel>
              <Button size="small" onClick={handleClearRecentSearches} sx={{ color: DesignSystem.colors.primary, textTransform: 'none', fontWeight: 600 }}>
                {t('searchClearAll')}
              </Button>
            </Box>
            {recentSearches
              .filter((s) => !isTestEntityName(s.query))
              .slice(0, 5)
              .map((search) => (
              <EntityRow
                key={search.id}
                dense
                onClick={() => updateQuery(search.query)}
                avatar={<History sx={{ color: DesignSystem.colors.textSecondary, fontSize: 22, mx: 1 }} />}
                title={search.query}
                trailing={
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRemoveRecentSearch(search.id); }}>
                    <Close sx={{ fontSize: 18 }} />
                  </IconButton>
                }
              />
            ))}
          </Box>
        )}

        {!searchQuery && recentSearches.length === 0 && businesses.length === 0 && !suggestedLoading && !isLoading && (
          <EmptyState icon={<Search />} title={t('searchEmptyTitle')} body={t('searchEmptyBody')} />
        )}

        {/* VISI Tab Content */}
        <TabPanel value={tabValue} index={0}>
          {isLoading || (!searchQuery && suggestedLoading) ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 1 }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={56} sx={{ bgcolor: DesignSystem.colors.surface }} />
              ))}
            </Box>
          ) : searchQuery && searchResults.length > 0 ? (
            <List disablePadding sx={{ px: 0 }}>
              {searchResults.map((business) => (
                <ListItem key={business.id} disablePadding>
                  <ListItemButton
                    onClick={() => handleBusinessClick(business)}
                    sx={{
                      py: 1.5,
                      borderRadius: DesignSystem.borderRadius.md,
                      mb: 1,
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={business.logo ? getBusinessLogoUrl(business.logo) || undefined : undefined}
                        sx={{
                          width: 48,
                          height: 48,
                          backgroundColor: DesignSystem.colors.surfaceElevated,
                        }}
                      >
                        {business.name?.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={business.name}
                      secondary={business.subcategory?.name || business.category?.name}
                      primaryTypographyProps={{
                        fontWeight: 600,
                        fontSize: DesignSystem.typography.body.fontSize,
                      }}
                      secondaryTypographyProps={{
                        fontSize: DesignSystem.typography.caption.fontSize,
                        color: DesignSystem.colors.textSecondary,
                      }}
                    />
                    {formatDistanceKm(business.distance) && (
                      <Box sx={{ display: 'flex', alignItems: 'center', color: DesignSystem.colors.primary }}>
                        <LocationOn sx={{ fontSize: 16, mr: 0.5 }} />
                        <Typography variant="caption">
                          {formatDistanceKm(business.distance)}
                        </Typography>
                      </Box>
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          ) : searchQuery ? (
            <EmptyState title={t('searchNoVisisFor', { q: searchQuery })} />
          ) : businesses.length > 0 ? (
            <Box sx={{ px: 0, pb: 2 }}>
              <SectionLabel>{t('searchSuggested')}</SectionLabel>
              {preferNonTestEntities(businesses, 8).map((business) => (
                <EntityRow
                  key={business.id}
                  dense
                  onClick={() => handleBusinessClick(business)}
                  avatarSrc={business.logo ? getBusinessLogoUrl(business.logo) || undefined : undefined}
                  avatarLetter={business.name?.charAt(0)}
                  title={business.name || 'VISI'}
                  meta={business.subcategory?.name || business.category?.name}
                />
              ))}
            </Box>
          ) : null}
        </TabPanel>

        {/* Categories Tab Content */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ px: 2, py: 2 }}>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredCategories.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {filteredCategories.map((category) => (
                  <Chip
                    key={category.id}
                    label={category.name}
                    onClick={() => handleCategoryClick(category.id)}
                    avatar={
                      category.icon ? (
                        <Avatar src={category.icon} sx={{ width: 24, height: 24 }} />
                      ) : undefined
                    }
                    sx={{
                      backgroundColor: DesignSystem.colors.surfaceElevated,
                      border: `1px solid ${DesignSystem.colors.separator}`,
                      '&:hover': {
                        backgroundColor: DesignSystem.colors.primaryLight,
                        borderColor: DesignSystem.colors.primary,
                      },
                    }}
                  />
                ))}
              </Box>
            ) : searchQuery ? (
              <Box sx={{
                textAlign: 'center',
                py: 8,
                color: DesignSystem.colors.textSecondary,
              }}>
                <Typography variant="body1">
                  {t('searchNoCategoriesFor', { q: searchQuery })}
                </Typography>
              </Box>
            ) : (
              <Box sx={{
                textAlign: 'center',
                py: 8,
                color: DesignSystem.colors.textSecondary,
              }}>
                <TrendingUp sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                <Typography variant="body1">
                  {t('searchNoCategoriesAvailable')}
                </Typography>
              </Box>
            )}
          </Box>
        </TabPanel>
    </AppShell>
  );
}
